import { useState, useCallback } from 'react';
import { chatbotApi } from '../services/chatbotApi';
import { useTranslation } from 'react-i18next';

const useChatSession = () => {
  const { i18n } = useTranslation();
  const [session, setSession] = useState(() => {
    try {
      const storedSession = sessionStorage.getItem('chatbotSession');
      return storedSession ? JSON.parse(storedSession) : null;
    } catch (error) {
      console.error("Could not parse chatbot session from sessionStorage", error);
      return null;
    }
  });
  const [sessionLoading, setSessionLoading] = useState(false);
  const [error, setError] = useState(null);

  // Create new session
  const createSession = useCallback(async (sessionType = 'general', contextData = {}) => {
    try {
      setSessionLoading(true);
      setError(null);

      const newSession = await chatbotApi.createSession({
        session_type: sessionType,
        context_data: contextData,
        language: i18n.language
      });

      setSession(newSession);
      try {
        sessionStorage.setItem('chatbotSession', JSON.stringify(newSession));
      } catch (error) {
        console.error("Could not save chatbot session to sessionStorage", error);
      }
      return newSession;

    } catch (err) {
      console.error('Error creating session:', err);
      setError(err.message || 'Failed to create session');
      throw err;
    } finally {
      setSessionLoading(false);
    }
  }, [i18n.language]);

  // Load existing session
  const loadSession = useCallback(async (sessionId) => {
    try {
      setSessionLoading(true);
      setError(null);

      const sessionData = await chatbotApi.getSession(sessionId);
      setSession(sessionData);
      try {
        sessionStorage.setItem('chatbotSession', JSON.stringify(sessionData));
      } catch (error) {
        console.error("Could not save chatbot session to sessionStorage", error);
      }
      return sessionData;

    } catch (err) {
      console.error('Error loading session:', err);
      setError(err.message || 'Failed to load session');
      throw err;
    } finally {
      setSessionLoading(false);
    }
  }, []);

  // Update session context
  const updateSessionContext = useCallback(async (sessionId, contextData) => {
    try {
      const updatedSession = await chatbotApi.updateSessionContext(sessionId, contextData);
      setSession(updatedSession);
      try {
        sessionStorage.setItem('chatbotSession', JSON.stringify(updatedSession));
      } catch (error) {
        console.error("Could not save chatbot session to sessionStorage", error);
      }
      return updatedSession;
    } catch (err) {
      console.error('Error updating session context:', err);
      setError(err.message || 'Failed to update session context');
      throw err;
    }
  }, []);

  // Clear session
  const clearSession = useCallback(() => {
    try {
      sessionStorage.removeItem('chatbotSession');
    } catch (error) {
      console.error("Could not remove chatbot session from sessionStorage", error);
    }
    setSession(null);
    setError(null);
  }, []);

  return {
    session,
    sessionLoading,
    error,
    createSession,
    loadSession,
    updateSessionContext,
    clearSession
  };
};

export { useChatSession };
